import { AlignLeft, Database, Info } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Table, type TableColumn } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Label } from '@/_shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { formatSql } from '@/_shared/lib/sql';

import type { WarehouseOption } from '../hooks/useWarehouseOptions';
import { hasStepPreview } from '../lib/step-preview';
import type { SampleRow, StepStatus, TestRunResult } from '../types';
import { SqlEditor } from './SqlEditor';
import { StepCard } from './StepCard';
import { StepResult } from './StepResult';

interface QueryStepProps {
  warehouseId: string;
  sql: string;
  /** Per-step Test-run state. */
  status: StepStatus;
  /** Query slice of the latest Dry run; `null` before any run. */
  result: TestRunResult['query'] | null;
  /** Dry-run error message to display inline (BAD_REQUEST from server). */
  error?: string | null;
  onWarehouseChange: (id: string) => void;
  onSqlChange: (sql: string) => void;
  /** Form-validation errors — shown inline in danger once touched/submitted. */
  warehouseError?: string;
  sqlError?: string;
  /** Mark the SQL field touched (on blur) so its error may show. */
  onSqlBlur?: () => void;
  /** Real warehouse options from settings.warehouses.list. */
  warehouseOptions: WarehouseOption[];
  /** True when the warehouse list is loading. */
  warehouseOptionsLoading?: boolean;
  /**
   * Read-only while the RoutineBuilder agent is mid-turn (it owns the draft during its turn); the human
   * edits once the agent is idle (v2 §0). When set, the warehouse picker and SQL field are display-only.
   */
  readOnly?: boolean;
  /** Flash the card's highlight when the agent freshly fills this step (§6.5). */
  highlight?: boolean;
}

export function QueryStep({
  warehouseId,
  sql,
  status,
  result,
  error,
  onWarehouseChange,
  onSqlChange,
  warehouseError,
  sqlError,
  onSqlBlur,
  warehouseOptions,
  warehouseOptionsLoading,
  readOnly = false,
  highlight = false,
}: QueryStepProps): React.ReactElement {
  const { wp } = useWorkspace();

  const previewColumns: TableColumn<SampleRow>[] = (result?.sampleColumns ?? []).map((col) => ({
    id: col,
    header: col,
  }));

  return (
    <StepCard
      stepNumber={1}
      kind="Query"
      title="Query warehouse"
      icon={Database}
      highlight={highlight}
      hasError={!!error}
      footerStale={status === 'stale'}
      footer={
        hasStepPreview(status, result) ? (
          <StepResult status={status}>
            {result != null && (
              <Table
                data={result.sampleRows}
                columns={previewColumns}
                totalRows={result.rowCount}
                showRowCount
                maxHeight="16rem"
              />
            )}
          </StepResult>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="query-warehouse">Warehouse</Label>
        {!warehouseOptionsLoading && warehouseOptions.length === 0 ? (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            No connected warehouses.{' '}
            <Link
              to={wp('/settings/warehouses')}
              className="text-primary underline underline-offset-2 hover:text-primary/80"
            >
              Connect a warehouse in Settings
            </Link>{' '}
            to run queries.
          </div>
        ) : (
          <Select
            value={warehouseId}
            onValueChange={onWarehouseChange}
            disabled={Boolean(warehouseOptionsLoading) || readOnly}
          >
            <SelectTrigger id="query-warehouse" aria-invalid={!!warehouseError} className="w-full">
              <SelectValue placeholder={warehouseOptionsLoading ? 'Loading…' : 'Select a warehouse'} />
            </SelectTrigger>
            <SelectContent>
              {warehouseOptions.map((w) => (
                <SelectItem key={w.value} value={w.value}>
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {warehouseError && (
          <p className="text-xs font-medium text-danger" role="alert">
            {warehouseError}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="query-sql">SQL</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Time-window and LIMIT help"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-relaxed">
                <p>
                  Add an explicit <code className="font-mono">LIMIT</code> (e.g.{' '}
                  <code className="font-mono">LIMIT 100</code>) — it&apos;s required.
                </p>
                <p className="mt-1.5">
                  <code className="font-mono">{'{{range_start}}'}</code> and{' '}
                  <code className="font-mono">{'{{range_end}}'}</code> bind the run&apos;s time window — the half-open
                  range <span className="font-mono">[start, end)</span> for this fire (e.g. the previous day for a daily
                  schedule). Put them in your <code className="font-mono">WHERE</code> clause to filter to the window.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          {!readOnly && (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              Icon={<AlignLeft />}
              onClick={() => {
                // Only emit when formatting actually changes something — an idempotent no-op would
                // otherwise flip the routine to "unsaved", trigger an autosave, and stale every preview.
                const formatted = formatSql(sql);
                if (formatted !== sql) onSqlChange(formatted);
              }}
              disabled={!sql.trim()}
            >
              Format
            </Button>
          )}
        </div>
        <SqlEditor
          id="query-sql"
          value={sql}
          onChange={onSqlChange}
          onBlur={onSqlBlur}
          invalid={!!sqlError}
          readOnly={readOnly}
        />
        {sqlError && (
          <p className="text-xs font-medium text-danger" role="alert">
            {sqlError}
          </p>
        )}
      </div>

      {/* role="alert" so the step-specific dry-run reason is announced — the top-level dry-run banner
          is only a count summary on the Zod path, so it doesn't cover this. */}
      {error && (
        <Callout variant="error" role="alert" className="whitespace-pre-line">
          {error}
        </Callout>
      )}
    </StepCard>
  );
}
